import { useQuery, QueryClient, UseQueryResult } from '@tanstack/react-query';
import { api } from '../api/client';

// Shared shape used by every dropdown_options consumer across Inventory.tsx, Settings.tsx,
// FacilityMap.tsx, RoomDetailPanel.tsx, Tasks.tsx.
export interface DropdownOption {
  id: number;
  label: string;
  value: string;
  is_active?: boolean;
  parent_id?: number | null;
}

export interface UseDropdownOptionsParams {
  facilityId?: number | null;
  parentId?: number | null;
  orgId?: number | null;
  includeInactive?: boolean;
  enabled?: boolean;
}

// Canonical query key builder — every caller that wants cache-sharing / invalidation to work
// must go through this (or useDropdownOptions below) rather than inventing its own key.
export function dropdownOptionsKey(
  dropdown_type: string | null | undefined,
  params?: Pick<UseDropdownOptionsParams, 'facilityId' | 'parentId' | 'orgId' | 'includeInactive'>
) {
  return [
    'dropdown-options',
    dropdown_type ?? null,
    params?.facilityId ?? null,
    params?.parentId ?? null,
    params?.orgId ?? null,
    // include_inactive changes the result set for the same type/facility/parent/org, so it must
    // be part of the key — otherwise an active-only fetch (combobox) and an include-inactive
    // fetch (Settings manager) for the same dropdown_type would collide on one cache entry and
    // whichever ran last would silently serve its result set to the other.
    params?.includeInactive ?? false,
  ] as const;
}

/**
 * Shared react-query hook for GET /dropdowns. Any new consumer should use this instead of a
 * bespoke useQuery/useEffect+api.get so new options created elsewhere invalidate this cache too.
 * See invalidateDropdowns() below — call it after every /dropdowns POST/PUT/PATCH/DELETE and
 * after any save that can auto-create a brand/model dropdown option server-side.
 */
export function useDropdownOptions(
  dropdown_type: string | null | undefined,
  params?: UseDropdownOptionsParams
): UseQueryResult<DropdownOption[]> {
  const { facilityId, parentId, orgId, includeInactive, enabled = true } = params ?? {};
  return useQuery({
    queryKey: dropdownOptionsKey(dropdown_type, { facilityId, parentId, orgId, includeInactive }),
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('dropdown_type', dropdown_type as string);
      if (facilityId != null) qs.set('facility_id', String(facilityId));
      if (parentId != null) qs.set('parent_id', String(parentId));
      if (orgId != null) qs.set('org_id', String(orgId));
      if (includeInactive) qs.set('include_inactive', 'true');
      const res = await api.get<{ options: DropdownOption[] }>(`/dropdowns?${qs.toString()}`);
      return (res as unknown as { options: DropdownOption[] }).options ?? [];
    },
    enabled: !!dropdown_type && enabled,
  });
}

// Legacy query keys that still need invalidating alongside ['dropdown-options', type] because
// they aren't read through useDropdownOptions. Keep this list in sync until each is migrated —
// invalidateDropdowns() invalidates all of them unconditionally (cheap no-op if the key isn't
// mounted) so nothing goes stale in the meantime. Remove an entry here once migrated.
//
// concentrator-brands / hvac-brands / brands-for-type / wc-brands-for-match / wc-models /
// dropdown-brands-all / dropdown-models-all / eq-brands-* / eq-models-* / dropdown-areas-all /
// dropdown-locations-all / dropdown-ticket-items (all formerly in Inventory.tsx / Settings.tsx)
// have been migrated to useDropdownOptions and removed from this list.
const LEGACY_DROPDOWN_QUERY_KEYS: ReadonlyArray<readonly unknown[]> = [
  ['bom-equipment-types'],        // GET /bom-equipment merges dropdown_options brand/model rows
                                   // server-side (inventory.ts) — a new brand/model must
                                   // invalidate it too until it reads through here
  ['ticket-items'],                // Tasks.tsx work-order form — read-only consumer, not migrated
  ['dropdown-areas'],              // Tasks.tsx work-order form (area select)
  ['dropdown-areas-multiselect'],  // Tasks.tsx fire-drill multiselect
  ['dropdown-locations'],          // Tasks.tsx work-order form (location select)
  ['dropdown-locations-multiselect'], // Tasks.tsx fire-drill multiselect
  ['dropdown-locations-edit'],     // Tasks.tsx work-order edit form (location select)
];

/**
 * Call after every /dropdowns POST/PUT/PATCH/DELETE (Inventory.tsx, Settings.tsx) and after any
 * save that can auto-create a brand/model dropdown option server-side (supply create/update,
 * BOM/equipment create/update, concentrator/HVAC/wheelchair forms).
 *
 * dropdownType, when known, narrows the ['dropdown-options', type] invalidation to that type's
 * cached queries (every facilityId/parentId/orgId variant — react-query prefix-matches). Omit it
 * to invalidate every dropdown-options query regardless of type (e.g. a save whose exact
 * dropdown_type isn't known at the call site).
 */
export function invalidateDropdowns(qc: QueryClient, dropdownType?: string) {
  qc.invalidateQueries({ queryKey: dropdownType ? ['dropdown-options', dropdownType] : ['dropdown-options'] });
  for (const key of LEGACY_DROPDOWN_QUERY_KEYS) {
    qc.invalidateQueries({ queryKey: key as unknown[] });
  }
}
